/**
 * WeatherCMSPage · v2.7.33 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Admin → Local Weather CMS editor — manages the public-facing
 * /local-weather page's description text + Taxi Weather Commander logo.
 *
 *   • Description: instant rich-text editor (saved on blur or "Save")
 *   • Logo: up to 100 MB image/pdf upload via multipart streaming
 *   • Save Changes: binds both updates atomically and reflects on /local-weather
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Palette, Upload, Trash2, ExternalLink, RefreshCw, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

const MAX_BYTES = 100 * 1024 * 1024;

export default function WeatherCMSPage() {
  const [cfg, setCfg] = useState(null);
  const [description, setDescription] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/weather/cms/config");
      setCfg(r.data);
      setDescription(r.data?.description || "");
      setDirty(false);
    } catch (e) {
      toast.error(`CMS load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const saveDescription = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      await adminApi.put("/admin/weather/cms/config", { description });
      toast.success("Description saved — published to /local-weather");
      reload();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`File too large — limit is ${(MAX_BYTES / (1024 * 1024)).toFixed(0)} MB`);
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      await adminApi.post("/admin/weather/cms/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success(`Logo uploaded · ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      reload();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteLogo = async () => {
    if (!window.confirm("Remove the current logo? The public page will revert to a text-only header.")) return;
    setBusy(true);
    try {
      await adminApi.delete("/admin/weather/cms/logo");
      toast.success("Logo removed");
      reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 p-4 sm:p-6 lg:p-8" data-testid="weather-cms-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/weather" className="text-amber-400 hover:text-amber-300 flex items-center gap-1 text-sm font-bold uppercase tracking-wider">
              <ArrowLeft className="h-4 w-4"/> Weather Commander
            </Link>
            <div className="h-6 w-px bg-zinc-800"/>
            <Palette className="h-6 w-6 text-fuchsia-300"/>
            <h1 className="text-2xl font-extrabold tracking-tight">Local Weather CMS</h1>
            <Badge className="bg-zinc-900 border-zinc-700 text-zinc-300 text-[10px] uppercase tracking-widest">Public-facing</Badge>
          </div>
          <div className="flex gap-2">
            <Button onClick={reload} variant="ghost" className="text-zinc-300" data-testid="cms-refresh"><RefreshCw className="h-4 w-4 mr-1.5"/>Reload</Button>
            <Button asChild className="bg-sky-500 text-white hover:bg-sky-400 font-bold">
              <a href="/local-weather" target="_blank" rel="noreferrer" data-testid="cms-preview-link"><ExternalLink className="h-4 w-4 mr-1.5"/>Preview public page</a>
            </Button>
          </div>
        </div>

        {/* Description editor */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-amber-400/40">
            Operational description · shows above radar on /local-weather
          </div>
          <Textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
            rows={6}
            placeholder="Enter the operational weather description shown on the public Local Weather page…"
            className="bg-zinc-950 border-zinc-700 text-sm"
            data-testid="cms-description-input"
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <span>{description.length} / 4000 chars · last updated {cfg?.updated_at ? new Date(cfg.updated_at).toLocaleString() : "—"}</span>
            <Button onClick={saveDescription} disabled={!dirty || busy} className="bg-amber-400 text-zinc-900 hover:bg-amber-300 font-bold" data-testid="cms-save-description">
              {busy ? "Saving…" : dirty ? "Save Changes" : "Saved"}
            </Button>
          </div>
        </Card>

        {/* Logo uploader */}
        <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-fuchsia-400/40">
            Taxi Weather Commander · brand logo (max 100 MB)
          </div>
          {cfg?.logo_url ? (
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-center bg-zinc-950 border border-zinc-800 rounded-lg p-4">
              <img src={cfg.logo_url} alt={cfg.logo_filename || "logo"} className="max-w-[160px] max-h-[80px] object-contain bg-white rounded p-2" data-testid="cms-current-logo"/>
              <div className="space-y-1 text-xs min-w-0">
                <div className="font-bold text-zinc-100 truncate">{cfg.logo_filename}</div>
                <div className="text-zinc-500">{cfg.logo_mime} · {cfg.logo_size ? `${(cfg.logo_size / 1024).toFixed(1)} KB` : "—"}</div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm" className="bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40 hover:bg-fuchsia-500/30 text-xs h-8 font-bold uppercase" data-testid="cms-replace-logo">
                    <Upload className="h-3.5 w-3.5 mr-1.5"/>Replace
                  </Button>
                  <Button onClick={deleteLogo} disabled={busy} size="sm" variant="ghost" className="text-red-300 hover:text-red-200 text-xs h-8 font-bold uppercase" data-testid="cms-delete-logo">
                    <Trash2 className="h-3.5 w-3.5 mr-1.5"/>Remove
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-zinc-700 hover:border-fuchsia-400/60 rounded-lg p-8 text-center bg-zinc-950 transition-colors"
              data-testid="cms-logo-dropzone"
            >
              <ImageIcon className="h-8 w-8 mx-auto text-zinc-600 mb-2"/>
              <div className="text-sm font-bold text-zinc-300">Click to upload Taxi Weather Commander logo</div>
              <div className="text-[10px] text-zinc-500 mt-1">Up to 100 MB · PNG / JPG / SVG / WebP / PDF</div>
            </button>
          )}
          {uploading && (
            <div className="text-xs text-zinc-400">
              <div className="flex items-center justify-between mb-1">
                <span>Uploading…</span>
                <span className="tabular-nums">{progress}%</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                <div className="h-full bg-fuchsia-400 transition-all" style={{ width: `${progress}%` }}/>
              </div>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="cms-logo-input"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadLogo(f);
            }}
          />
          <div className="text-[10px] text-zinc-500 leading-relaxed">
            CSS constraints on /local-weather lock the rendered logo to <b>max-width 200px · max-height 64px</b> so high-resolution masters render as a clean badge.
          </div>
        </Card>

        {/* v2.7.35 · Forecast panel background slots (8-day + 12-day) */}
        <ForecastBgSlot slot="forecast_8day_bg"  label="8-Day Forecast · panel background" cfg={cfg} reload={reload}/>
        <ForecastBgSlot slot="forecast_12day_bg" label="12-Day Forecast · panel background" cfg={cfg} reload={reload}/>
      </div>
    </div>
  );
}

// v2.7.35 · Reusable named-slot uploader (100MB max via GridFS)
function ForecastBgSlot({ slot, label, cfg, reload }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);
  const url = cfg?.asset_urls?.[slot] || null;
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = url ? `${apiBase}${url}` : null;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) { toast.error(`File too large — max ${(MAX_BYTES / 1024 / 1024).toFixed(0)} MB`); return; }
    setUploading(true); setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      await adminApi.post(`/admin/weather/cms/assets/${slot}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      toast.success(`${label} updated`);
      reload();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false); setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the ${label}?`)) return;
    try {
      await adminApi.delete(`/admin/weather/cms/assets/${slot}`);
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error(`Remove failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 space-y-3" data-testid={`cms-slot-${slot}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold pb-1.5 border-b border-sky-400/40 flex items-center justify-between">
        <span>{label}</span>
        <Badge className="bg-zinc-950 border-zinc-700 text-zinc-300 text-[9px]">slot: {slot}</Badge>
      </div>
      {fullUrl ? (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center bg-zinc-950 border border-zinc-800 rounded-lg p-3">
          <div className="aspect-[16/4] w-full overflow-hidden rounded border border-zinc-800" style={{ backgroundImage: `url("${fullUrl}")`, backgroundSize: "cover", backgroundPosition: "center" }}/>
          <div className="flex gap-2">
            <Button onClick={() => inputRef.current?.click()} disabled={uploading} size="sm" className="bg-sky-500/20 text-sky-200 border border-sky-400/40 hover:bg-sky-500/30 text-xs h-8 font-bold uppercase" data-testid={`cms-${slot}-replace`}>
              <Upload className="h-3.5 w-3.5 mr-1.5"/>Replace
            </Button>
            <Button onClick={remove} size="sm" variant="ghost" className="text-red-300 hover:text-red-200 text-xs h-8 font-bold uppercase" data-testid={`cms-${slot}-remove`}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5"/>Remove
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full border-2 border-dashed border-zinc-700 hover:border-sky-400/60 rounded-lg p-6 text-center bg-zinc-950 transition-colors" data-testid={`cms-${slot}-dropzone`}>
          <ImageIcon className="h-6 w-6 mx-auto text-zinc-600 mb-1"/>
          <div className="text-xs font-bold text-zinc-300">Click to upload {label}</div>
          <div className="text-[10px] text-zinc-500 mt-1">Up to 100 MB · PNG / JPG / SVG / WebP</div>
        </button>
      )}
      {uploading && (
        <div className="text-xs text-zinc-400">
          <div className="flex justify-between mb-1"><span>Uploading…</span><span className="tabular-nums">{progress}%</span></div>
          <div className="h-1.5 bg-zinc-800 rounded overflow-hidden"><div className="h-full bg-sky-400 transition-all" style={{ width: `${progress}%` }}/></div>
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} data-testid={`cms-${slot}-input`}/>
    </Card>
  );
}
